/*
 * Copyright (c) 2021 SGS Europe. All rights reserved.
 *
 * This program is the CONFIDENTIAL and PROPRIETARY property of SGS Europe.
 * Any unauthorised use, reproduction, or transfer of this program is strictly
 * prohibited.
 *
 * File Name: GreenlightTextTools.js
 *
 * Description:	The class containing the functionality required by the Greenlight for text tools.
 * 
 */

// The text tools object.
var g_oTextTools = {};

(function () {
	"use strict";

	/**
	 * Load the text content for the page.
	 */
	function TextTools() {

		// The tools available for use.
		this.g_eToolTypes = {
			TextHighlight: 1,
			ParagraphHighlight: 2,
			RemoveTextHighlight: 3,
			AreaTextHighlight: 4
		};

		// Keeps track of if a highlight is in progress.
		this.g_bHighlightInProgress = false;

		// Keep track of the highlight accuracy.
		this.g_bHighlightIsCharacterAccuracy = false;

		// Keeps track of if the highlight is on the compare canvas.
		this.g_bHighlightOnCompareCanvas = false;

		// Keep track of the active tool type.
		this.g_eActiveToolType = null;

		// Keep track of the active annotation type.
		this.g_eActiveAnnotationType = null;

		// The annotation text for the highlight.
		this.g_oAnnotationText = [];

		// The current text on highlight.
		this.g_oCurrentText = null;

		// The starting text on highlight.
		this.g_oStartingText = null;

		// The ending text on highlight.
		this.g_oEndingText = null;

		// The colour to use for highlighting.
		this.g_szHighlightColour = "";

		// The annotation to add text to.
		this.g_iExistingAnnotationId = null;
	};

	/**
	 * Initialises a text tool.
	 * @param {enum} eToolType - The tool to be initialised.
	 * @param {enum} eAnnotationType - The annotation type.
	 * @param {bool} bCharacterLevelAccuracy - If the tool should be at character or word level accuracy.
	 * @param {bool} bCompareCanvas - If this is on the compare canvas.
	 * @param {string} colour - The colour to be used.
	 */
	TextTools.prototype.initialiseTextTool = function (eToolType, eAnnotationType, bCharacterLevelAccuracy, bCompareCanvas, colour) {
		
		// Set that a highlight is in progress.
		g_oTextTools.g_bHighlightInProgress = true;

		// Update the highlight settings.
		g_oTextTools.g_eActiveToolType = eToolType;
		g_oTextTools.g_eActiveAnnotationType = eAnnotationType;
		g_oTextTools.g_bHighlightOnCompareCanvas = bCompareCanvas;
		g_oTextTools.g_bHighlightIsCharacterAccuracy = bCharacterLevelAccuracy;

		// Update the highlight colour.
		g_oTextTools.setHighlightColour(colour);

		// If this is an edit for a new annotation, remove the div without deleting the annotation.
		if (g_oTextTools.g_iExistingAnnotationId < 0) {
			$("#" + g_oTextTools.g_iExistingAnnotationId).remove();
		}

		// Close any other open annotations.
		g_oAnnotations.closeActiveAnnotations();

		// Hide the existing annotations from the annotation list.
		$(".annotationListItem").remove();

		// Display an info bubble for highlighting text if required.
		if ($(".speechbubble__texthighlight").length == 0) {
			g_oTextTools.displayHighlightInfo();
		}

		// Reshow annotations if they are hidden.
		if (!g_oSettings.g_oUserSettings.ShowAnnotations) {
			$("#HideAnnotations").click();
		}

		// Clear all bindings.
		changeCanvasFunction();

		// Ensure we are on the main canvas.
		if ($("#chkAnnotationToggle").prop("checked")) {
			$("#chkAnnotationToggle").click();
		}

		// Initialise the tool.
		switch (g_oTextTools.g_eActiveToolType) {
			case g_oTextTools.g_eToolTypes.TextHighlight:
				g_oTextTools_TextHighlight.initialiseHighlight();
				break;
			case g_oTextTools.g_eToolTypes.ParagraphHighlight:
				g_oTextTools_ParagraphHighlight.initialiseHighlight();
				break;
			case g_oTextTools.g_eToolTypes.RemoveTextHighlight:
				g_oTextTools_RemoveTextHighlight.initialiseHighlight();
				break;
			case g_oTextTools.g_eToolTypes.AreaTextHighlight:
				g_oTextTools_AreaTextHighlight.initialiseHighlight();
				break;
		}
	};

	/**
	 * Ends a text highlight.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @param {bool} bSaveAnnotation - If the highlight annotation should be saved.
	 */
	TextTools.prototype.endhighlight = function (oArtworkCanvas, oAnnotationCanvas, bSaveAnnotation) {

		// Set that a highlight is not in progress.
		g_oTextTools.g_bHighlightInProgress = false;

		// Close any open info bubbles.
		g_oGreenlightMessages.closeBubbles("Info");

		// If we do not want to save the annotation, stop the process here.
		if (!bSaveAnnotation) {
			return;
		}

		// If this is part of a text compare, send the annotation text back to the text compare.
		if (g_oTextCompare.g_bTextCompareInProgress) {

			// Reset the canvas function.
			changeCanvasFunction();

			// Should we auto select the text on the compare document.
			var bAutoSelect = g_oTextCompare_AutoSelection.isAutoSelectionEnabled();

			// If this is a new annotation.
			if (g_oTextTools.g_iExistingAnnotationId == null) {
				g_oTextCompare.addTextData(g_oTextTools.g_oAnnotationText, g_oTextTools.g_bHighlightOnCompareCanvas, bAutoSelect, true);
			}
			// If this is an existing annotation.
			else {
				g_oTextCompare.updateTextData(g_oTextTools.g_oAnnotationText, g_oTextTools.g_bHighlightOnCompareCanvas, bAutoSelect);
			}
			return;
		}

		// Re-build the annotation list.
		g_oAnnotations.rebuildAnnotationList(oArtworkCanvas, oAnnotationCanvas);

		// Check we have highlight content.
		if (g_oTextTools.g_oAnnotationText.length == 0) {
			return;
		}

		// If this is a new annotation.
		if (g_oTextTools.g_iExistingAnnotationId == null) {

			// Create the annotation.
			g_oTextTools.createAnnotation(oArtworkCanvas, oAnnotationCanvas);
		}
		// If this is an edit of a new annotation.
		else if (g_oTextTools.g_iExistingAnnotationId < 0) {

			// Update the new annotation.
			g_oTextTools.updateNewAnnotation(oArtworkCanvas, oAnnotationCanvas);
		}
		// If this is an existing annotation.
		else {

			// Update the annotation.
			g_oTextTools.updateAnnotation(oArtworkCanvas, oAnnotationCanvas);
		}
	};

	/**
	 * Converts a mouse position to percentages.
	 * @param {object} event - The event.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The mouse percentages.
	 */
	TextTools.prototype.convertMousePositionToPercentages = function (event, oArtworkCanvas, oAnnotationCanvas) {

		// Get the click coordinates.
		var coords = g_oAnnotations.getClick(event, oAnnotationCanvas);

		// Get the transformed coordinates.
		var transformedClick = g_oAnnotations.transformClickForRotation(coords[0], coords[1], oArtworkCanvas);
		coords[0] = transformedClick[0];
		coords[1] = transformedClick[1];

		// Calculate the compare offset of the canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Convert the coordinates to percentages.
		coords[0] = (coords[0] - compareOffset_x) / oArtworkCanvas.g_iScaledImageWidth;
		coords[1] = (coords[1] - compareOffset_y) / oArtworkCanvas.g_iScaledImageHeight;

		// Return the result.
		return {
			x: coords[0],
			y: coords[1]
		};
	};

	/**
	 * Displays the highlight info.
	 */
	TextTools.prototype.displayHighlightInfo = function () {
		
		// Hide any existing bubbles.
		for (var i = 0; i < g_oActiveBubbles.length; i++) {
			g_oGreenlightMessages.closeBubble(g_oActiveBubbles[i].id, true, true);
		}
		
		// Display the info bubble with the correct text.
		g_oGreenlightMessages.displayInfoBubble(g_oTextTools.getHighlightInfo(), true);
	};

	/**
	 * Gets the text highlight info.
	 */
	TextTools.prototype.getHighlightInfo = function () {
		
		// Outline the highlight info to be returned.
		var szHighlightInfo = "";

		// Open the main info div.
		szHighlightInfo += '<div class="speechbubble__texthighlight">';

		// Add the text based on the tool type.
		szHighlightInfo += '<div class="speechbubble__texthighlight--marginbottom">';
		switch (g_oTextTools.g_eActiveToolType) {
			case g_oTextTools.g_eToolTypes.TextHighlight:
				szHighlightInfo += g_oTextTools.c_szTextHighlight;
				break;
			case g_oTextTools.g_eToolTypes.ParagraphHighlight:
				szHighlightInfo += g_oTextTools.c_szParagraphHighlight;
				break;
			case g_oTextTools.g_eToolTypes.RemoveTextHighlight:
				szHighlightInfo += g_oTextTools.c_szRemoveTextHighlight;
				break;
			case g_oTextTools.g_eToolTypes.AreaTextHighlight:
				szHighlightInfo += g_oTextTools.c_szAreaTextHighlight;
				break;
		}
		szHighlightInfo += g_oTextTools.isTextHighlightLeftToRight() ? ' ' + g_oTextTools.c_szTextHighlightLeftToRight : ' ' + g_oTextTools.c_szTextHighlightRightToLeft;
		szHighlightInfo += '</div>';

		// Add the close message.
		szHighlightInfo += '<div class="speechbubble__texthighlight--inline">' + g_oTextTools.c_szTextHighlightExit + '</div>';

		// Add the see more message.
		szHighlightInfo += '<div id="divTextHighlightShowMoreInfo" class="speechbubble__texthighlight--inline speechbubble__texthighlight--marginleft speechbubble__texthighlight--clickable" onclick="g_oTextTools.showMoreHighlightInfo();">' + g_oTextTools.c_szTextHighlightSeeMore + '</div>';

		// Add the see more info to be shown.
		szHighlightInfo += '<div id="divTextHighlightMoreInfo" style="display: none;">';
		szHighlightInfo += '<div class="speechbubble__texthighlight--margintop">' + g_oTextTools.c_szTextHighlightClickDragRelease + '</div>';
		szHighlightInfo += '<div class="speechbubble__texthighlight--margintop">' + g_oTextTools.c_szTextHighlightChangeDirection + '</div>';
		szHighlightInfo += '</div>';

		// Close the main info div.
		szHighlightInfo += '</div>';

		// Return the highlight info.
		return szHighlightInfo;
	};

	/**
	 * Shows more highlight information.
	 */
	TextTools.prototype.showMoreHighlightInfo = function () {

		// Hide the show more content div.
		$("#divTextHighlightShowMoreInfo").hide();

		// Show the extra content.
		$("#divTextHighlightMoreInfo").slideToggle();
	};

	/**
	 * Toggles the highlight direction.
	 */
	TextTools.prototype.toggleHighlightDirection = function () {

		// Toggle the highlight direction.
		g_oSettings.g_oUserSettings.HighlightPreference = g_oSettings.g_oUserSettings.HighlightPreference == eHighlightType.LeftToRight ? eHighlightType.RightToLeft : eHighlightType.LeftToRight;

		// Update the setting.
		g_oSettings.updateSettingHighlightPreference(true);

		// Update the info bubble.
		g_oTextTools.displayHighlightInfo();
	};

	/**
	 * Gets the first text in a paragraph.
	 * @param {int} iParagraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getFirstTextInParagraph = function (iParagraph, oArtworkCanvas, oAnnotationCanvas) {
		return g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getFirstCharacterInParagraph(iParagraph, oArtworkCanvas) : g_oTextTools.getFirstWordInParagraph(iParagraph, oArtworkCanvas);
	};

	/**
	 * Gets the first word in a paragraph.
	 * @param {int} iParagraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getFirstWordInParagraph = function (iParagraph, oArtworkCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the first word of the paragraph.
		return {
			paragraph: oTextData.Paragraphs[iParagraph].Order,
			line: oTextData.Paragraphs[iParagraph].Lines[0].Order,
			word: oTextData.Paragraphs[iParagraph].Lines[0].Words[0].Order
		};
	};

	/**
	 * Gets the first character in a paragraph.
	 * @param {int} iParagraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getFirstCharacterInParagraph = function (iParagraph, oArtworkCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the first character of the paragraph.
		return {
			paragraph: oTextData.Paragraphs[iParagraph].Order,
			line: oTextData.Paragraphs[iParagraph].Lines[0].Order,
			word: oTextData.Paragraphs[iParagraph].Lines[0].Words[0].Order,
			character: oTextData.Paragraphs[iParagraph].Lines[0].Words[0].Characters[0].Order
		};
	};

	/**
	 * Gets the last text in a paragraph.
	 * @param {int} iParagraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getLastTextInParagraph = function (iParagraph, oArtworkCanvas, oAnnotationCanvas) {
		return g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getLastCharacterInParagraph(iParagraph, oArtworkCanvas) : g_oTextTools.getLastWordInParagraph(iParagraph, oArtworkCanvas);
	};

	/**
	 * Gets the last word in a paragraph.
	 * @param {int} iParagraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getLastWordInParagraph = function (iParagraph, oArtworkCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the last word of the paragraph.
		return {
			paragraph: oTextData.Paragraphs[iParagraph].Order,
			line: oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Order,
			word: oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words[oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words.length - 1].Order,
		};
	};

	/**
	 * Gets the last character in a paragraph.
	 * @param {int} iParagraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getLastCharacterInParagraph = function (iParagraph, oArtworkCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the last character of the paragraph.
		return {
			paragraph: oTextData.Paragraphs[iParagraph].Order,
			line: oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Order,
			word: oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words[oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words.length - 1].Order,
			character: oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words[oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words.length - 1].Characters[oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words[oTextData.Paragraphs[iParagraph].Lines[oTextData.Paragraphs[iParagraph].Lines.length - 1].Words.length - 1].Characters.length - 1].Order,
		};
	};

	/**
	 * Checks if the character is the last in the word.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {int} character - The character.
	 * @param {object} oTextData - The text data.
	 */
	TextTools.prototype.isLastCharacterInWord = function (paragraph, line, word, character, oTextData) {
		return oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters.length == character + 1;
	};

	/**
	 * Finds text within a point.
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The found text.
	 */
	TextTools.prototype.findText = function (point_x, point_y, oArtworkCanvas, oAnnotationCanvas) {
		return g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.findText_Character(point_x, point_y, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.findText_Word(point_x, point_y, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Finds text within a point at a word level.
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The found text.
	 */
	TextTools.prototype.findText_Word = function (point_x, point_y, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Try to find a paragragh.
		var iParagraph = g_oTextTools.findParagraph(point_x, point_y, oTextData);

		// If a paragraph has been found.
		if (iParagraph != -1) {

			// Try to find a line in the paragraph.
			var iLine = g_oTextTools.findLine(point_x, point_y, iParagraph, oTextData);

			// If a line has been found.
			if (iLine != -1) {

				// Try to find a word in the line.
				var iWord = g_oTextTools.findWord(point_x, point_y, iParagraph, iLine, oTextData);

				// If a word has been found.
				if (iWord != -1) {

					// Return the text.
					return {
						paragraph: iParagraph,
						line: iLine,
						word: iWord
					};
				}
			}
		}

		// No text found.
		return null;
	};

	/**
	 * Finds text within a point at a character level.
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The found text.
	 */
	TextTools.prototype.findText_Character = function (point_x, point_y, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Try to find a paragragh.
		var iParagraph = g_oTextTools.findParagraph(point_x, point_y, oTextData);

		// If a paragraph has been found.
		if (iParagraph != -1) {

			// Try to find a line in the paragraph.
			var iLine = g_oTextTools.findLine(point_x, point_y, iParagraph, oTextData);

			// If a line has been found.
			if (iLine != -1) {

				// Try to find a word in the line.
				var iWord = g_oTextTools.findWord(point_x, point_y, iParagraph, iLine, oTextData);

				// If a word has been found.
				if (iWord != -1) {

					// Try to find a character in the word.
					var iCharacter = g_oTextTools.findChararacter(point_x, point_y, iParagraph, iLine, iWord, oTextData);

					// If a character has been found.
					if (iCharacter != -1) {

						// Return the text.
						return {
							paragraph: iParagraph,
							line: iLine,
							word: iWord,
							character: iCharacter
						};
					}
				}
			}
		}

		// No text found.
		return null;
	};

	/**
	 * Finds a paragraph.
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {object} oTextData - The text data.
	 * @returns {int} - The found paragraph.
	 */
	TextTools.prototype.findParagraph = function (point_x, point_y, oTextData) {

		// Outline the paragraph to be returned.
		var iParagraph = -1;

		// Check for each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// If this point is within the bounds of the paragraph.
			if (g_oTextTools.isPointWithinBounds(point_x, point_y, oTextData.Paragraphs[i].X1, oTextData.Paragraphs[i].X2, oTextData.Paragraphs[i].Y1, oTextData.Paragraphs[i].Y2)) {

				// Set that the paragraph has been found.
				iParagraph = i;
				break;
			}
		}

		// Return the found paragraph.
		return iParagraph;
	};

	/**
	 * Finds a line,
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {int} paragraph - The paragraph.
	 * @param {object} oTextData - The text data.
	 * @returns {int} - The found line.
	 */
	TextTools.prototype.findLine = function (point_x, point_y, paragraph, oTextData) {

		// Outline the line to be returned.
		var iLine = -1;

		// Check for each of the lines.
		for (var i = 0; i < oTextData.Paragraphs[paragraph].Lines.length; i++) {

			// If this point is within the bounds of the line.
			if (g_oTextTools.isPointWithinBounds(point_x, point_y, oTextData.Paragraphs[paragraph].Lines[i].X1, oTextData.Paragraphs[paragraph].Lines[i].X2, oTextData.Paragraphs[paragraph].Lines[i].Y1, oTextData.Paragraphs[paragraph].Lines[i].Y2)) {

				// Set that the line has been found.
				iLine = i;
				break;
			}
		}

		// Return the found line.
		return iLine;
	};

	/**
	 * Finds a word.
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {object} oTextData - The text data.
	 * @returns {int} - The found word.
	 */
	TextTools.prototype.findWord = function (point_x, point_y, paragraph, line, oTextData) {

		// Outline the word to be returned.
		var iWord = -1;

		// Check for each of the words.
		for (var i = 0; i < oTextData.Paragraphs[paragraph].Lines[line].Words.length; i++) {

			// If this point is within the bounds of the word.
			if (g_oTextTools.isPointWithinBounds(point_x, point_y, oTextData.Paragraphs[paragraph].Lines[line].Words[i].X1, oTextData.Paragraphs[paragraph].Lines[line].Words[i].X2, oTextData.Paragraphs[paragraph].Lines[line].Words[i].Y1, oTextData.Paragraphs[paragraph].Lines[line].Words[i].Y2)) {

				// Set that the word has been found.
				iWord = i;
				break;
			}
		}

		// Return the found word.
		return iWord;
	};

	/**
	 * Finds a character.
	 * @param {double} point_x - The x position.
	 * @param {doube} point_y - The y position.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {object} oTextData - The text data.
	 * @returns {int} - The found character.
	 */
	TextTools.prototype.findChararacter = function (point_x, point_y, paragraph, line, word, oTextData) {

		// Outline the character to be returned.
		var iCharacter = -1;

		// Check for each of the characters.
		for (var i = 0; i < oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters.length; i++) {

			// If this point is within the bounds of the character.
			if (g_oTextTools.isPointWithinBounds(point_x, point_y, oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters[i].X1, oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters[i].X2, oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters[i].Y1, oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters[i].Y2)) {

				// Set that the character has been found.
				iCharacter = i;
				break;
			}
		}

		// Return the found character.
		return iCharacter;
	};

	/**
	 * Checks if a point is within bounds.
	 * @param {double} point_x - The x position.
	 * @param {double} point_y - The y position.
	 * @param {double} bounds_x1 - The bounds x1.
	 * @param {double} bounds_x2 - The bounds x2.
	 * @param {double} bounds_y1 - The bounds y1.
	 * @param {double} bounds_y2 - The bounds y2.
	 * @returns {bool} - If the point is within the bounds.
	 */
	TextTools.prototype.isPointWithinBounds = function (point_x, point_y, bounds_x1, bounds_x2, bounds_y1, bounds_y2) {
		return point_x >= bounds_x1 && point_x <= bounds_x2 && point_y >= bounds_y1 && point_y <= bounds_y2;
	};

	/**
	 * Finds the closest text to a point.
	 * @param {double} point_x - The point x.
	 * @param {double} point_y - The point y.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.findClosestText = function (point_x, point_y, oArtworkCanvas, oAnnotationCanvas) {
		return g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.findClosestText_Character(point_x, point_y, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.findClosestText_Word(point_x, point_y, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Finds the closest word to a point.
	 * @param {double} point_x - The point x.
	 * @param {double} point_y - The point y.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.findClosestText_Word = function (point_x, point_y, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the text to be returned, and the closest distance.
		var oText = null;
		var iClosestDistance = -1;

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Check for each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Check for each of the lines.
			for (var j = 0; j < oTextData.Paragraphs[i].Lines.length; j++) {

				// Check for each of the words.
				for (var k = 0; k < oTextData.Paragraphs[i].Lines[j].Words.length; k++) {

					// Get the distance to the bounds.
					var iDistance = g_oTextTools.distancePointToBounds(point_x, point_y, oTextData.Paragraphs[i].Lines[j].Words[k].X1, oTextData.Paragraphs[i].Lines[j].Words[k].X2, oTextData.Paragraphs[i].Lines[j].Words[k].Y1, oTextData.Paragraphs[i].Lines[j].Words[k].Y2);

					// If this point is closer to the bounds.
					if (oText == null || iDistance < iClosestDistance) {

						// Set that the word is the closest.
						oText = {
							paragraph: i,
							line: j,
							word: k
						};

						// Update the closest distance.
						iClosestDistance = iDistance;
					}
				}
			}
		}

		// Return the closest word.
		return oText;
	};

	/**
	 * Finds the closest character to a point.
	 * @param {double} point_x - The point x.
	 * @param {double} point_y - The point y.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.findClosestText_Character = function (point_x, point_y, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the text to be returned, and the closest distance.
		var oText = null;
		var iClosestDistance = -1;

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Check for each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Check for each of the lines.
			for (var j = 0; j < oTextData.Paragraphs[i].Lines.length; j++) {

				// Check for each of the words.
				for (var k = 0; k < oTextData.Paragraphs[i].Lines[j].Words.length; k++) {

					// Check for each of the characters.
					for (var l = 0; l < oTextData.Paragraphs[i].Lines[j].Words[k].Characters.length; l++) {

						// Get the distance to the bounds.
						var iDistance = g_oTextTools.distancePointToBounds(point_x, point_y, oTextData.Paragraphs[i].Lines[j].Words[k].Characters[l].X1, oTextData.Paragraphs[i].Lines[j].Words[k].Characters[l].X2, oTextData.Paragraphs[i].Lines[j].Words[k].Characters[l].Y1, oTextData.Paragraphs[i].Lines[j].Words[k].Characters[l].Y2);

						// If this point is closer to the bounds.
						if (oText == null || iDistance < iClosestDistance) {

							// Set that the character is the closest.
							oText = {
								paragraph: i,
								line: j,
								word: k,
								character: l
							};

							// Update the closest distance.
							iClosestDistance = iDistance;
						}
					}
				}
			}
		}

		// Return the closest character.
		return oText;
	};

	/**
	 * Gets the distance from a point to a bounds.
	 * @param {any} point_x - The point x.
	 * @param {any} point_y - The point y.
	 * @param {any} bounds_x1 - The x1 bounds.
	 * @param {any} bounds_x2 - The x2 bounds.
	 * @param {any} bounds_y1 - The y1 bounds.
	 * @param {any} bounds_y2 - The y2 bounds.
	 */
	TextTools.prototype.distancePointToBounds = function (point_x, point_y, bounds_x1, bounds_x2, bounds_y1, bounds_y2) {

		// Outline the mid points for comparison.
		var bounds_x_middle = bounds_x1 + ((bounds_x2 - bounds_x1) / 2);
		var bounds_y_middle = bounds_y1 + ((bounds_y2 - bounds_y1) / 2);

		// Return the distance to the closest point on the bounds.
		return Math.min(
			Math.sqrt(Math.pow((point_x - bounds_x1), 2) + Math.pow((point_y - bounds_y1), 2)), // Top Left Point
			Math.sqrt(Math.pow((point_x - bounds_x2), 2) + Math.pow((point_y - bounds_y1), 2)), // Top Right Point
			Math.sqrt(Math.pow((point_x - bounds_x1), 2) + Math.pow((point_y - bounds_y2), 2)), // Bottom Left Point
			Math.sqrt(Math.pow((point_x - bounds_x2), 2) + Math.pow((point_y - bounds_y2), 2)), // Bottom Right Point
			Math.sqrt(Math.pow((point_x - bounds_x_middle), 2) + Math.pow((point_y - bounds_y1), 2)), // Top Middle Point
			Math.sqrt(Math.pow((point_x - bounds_x_middle), 2) + Math.pow((point_y - bounds_y2), 2)), // Bottom Middle Point
			Math.sqrt(Math.pow((point_x - bounds_x1), 2) + Math.pow((point_y - bounds_y_middle), 2)), // Middle Left Point
			Math.sqrt(Math.pow((point_x - bounds_x2), 2) + Math.pow((point_y - bounds_y_middle), 2)) // Middle Right Point
		);
	};

	/**
	 * Shows all paragraphs.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.showAllParagraphs = function (oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Highlight each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {
			g_oTextTools.highlightParagraph(i, oArtworkCanvas, oAnnotationCanvas);
		}
	};

	/**
	 * Shows all lines.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.showAllLines = function (oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Highlight each of the lines.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {
			for (var j = 0; j < oTextData.Paragraphs[i].Lines.length; j++) {
				g_oTextTools.highlightLine(i, j, oArtworkCanvas, oAnnotationCanvas);
			}
		}
	};

	/**
	 * Shows all words.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.showAllWords = function (oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Highlight each of the words.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {
			for (var j = 0; j < oTextData.Paragraphs[i].Lines.length; j++) {
				for (var k = 0; k < oTextData.Paragraphs[i].Lines[j].Words.length; k++) {
					g_oTextTools.highlightWord(i, j, k, oArtworkCanvas, oAnnotationCanvas);
				}
			}
		}
	};

	/**
	 * Removes all highlights.
	 * @param {oAnnotationCanvas} - The annotation canvas to remove highlights for.
	 */
	TextTools.prototype.removeHighlights = function (oAnnotationCanvas) {
		oAnnotationCanvas.g_oContext.save();
		g_oMarquee.derotateCanvasContext(oAnnotationCanvas.g_oContext, oAnnotationCanvas, oAnnotationCanvas.g_oCanvas.width, oAnnotationCanvas.g_oCanvas.height, oAnnotationCanvas.g_oCanvas.width / 2, oAnnotationCanvas.g_oCanvas.height / 2);
		oAnnotationCanvas.g_oContext.clearRect(0, 0, oAnnotationCanvas.g_oCanvas.width, oAnnotationCanvas.g_oCanvas.height);
		oAnnotationCanvas.g_oContext.restore();
	};

	/**
	 * Highlights a list of sections.
	 * @param {object} oSections - The sections to highlight.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightSections = function (oSections, oArtworkCanvas, oAnnotationCanvas) {
		g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.highlightSections_Character(oSections, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.highlightSections_Word(oSections, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Highlights a list of sections at word level.
	 * @param {object} oSections - The sections to highlight.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightSections_Word = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Get the sections words.
		var oWords = g_oTextTools.getSectionsWords(oSections, oArtworkCanvas, oAnnotationCanvas);

		// Sort the sections words.
		oWords = oWords.sort(g_oTextTools.sortWords);

		// Remove the duplicate words.
		oWords = g_oTextTools.distinctWords(oWords);

		// Highlight each of the words.
		for (var i = 0; i < oWords.length; i++) {

			// If we have highlighted the previous word on this paragraph line.
			if (i > 0 && oWords[i].word == oWords[i - 1].word + 1 && oWords[i].line == oWords[i - 1].line && oWords[i].paragraph == oWords[i - 1].paragraph) {

				// Get the previous word.
				var oWord = oTextData.Paragraphs[oWords[i].paragraph].Lines[oWords[i].line].Words[oWords[i].word];
				var oPreviousWord = oTextData.Paragraphs[oWords[i - 1].paragraph].Lines[oWords[i - 1].line].Words[oWords[i - 1].word];

				// Determine the coordinates to be used for the space based on the text direction.
				var iX1 = oPreviousWord.X2 < oWord.X1 ? oPreviousWord.X2 : oPreviousWord.X1;
				var iX2 = oPreviousWord.X2 < oWord.X1 ? oWord.X1 : oWord.X2;
				var iY1 = oPreviousWord.Y1 < oWord.Y2 ? oPreviousWord.Y2 : oPreviousWord.Y1;
				var iY2 = oPreviousWord.Y1 < oWord.Y2 ? oWord.Y1 : oWord.Y2;

				// Highlight the space between the two words.
				g_oTextTools.highlightElement(iX1, iX2, iY1, iY2, oArtworkCanvas, oAnnotationCanvas);
			}

			// Highlight the word.
			g_oTextTools.highlightWord(oWords[i].paragraph, oWords[i].line, oWords[i].word, oArtworkCanvas, oAnnotationCanvas);
		}
	};

	/**
	 * Highlights a list of sections at character level.
	 * @param {object} oSections - The sections to highlight.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightSections_Character = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Get the sections characters.
		var oCharacters = g_oTextTools.getSectionsCharacters(oSections, oArtworkCanvas, oAnnotationCanvas);

		// Sort the sections characters.
		oCharacters = oCharacters.sort(g_oTextTools.sortCharacters);

		// Remove the duplicate characters.
		oCharacters = g_oTextTools.distinctCharacters(oCharacters);

		// Highlight each of the characters.
		for (var i = 0; i < oCharacters.length; i++) {
			
			// If we have highlighted the last character of the previous word on this paragraph line, and this is the first character of the next word.
			if (i > 0 && oCharacters[i].word == oCharacters[i - 1].word + 1 && oCharacters[i].line == oCharacters[i - 1].line && oCharacters[i].paragraph == oCharacters[i - 1].paragraph && g_oTextTools.isLastCharacterInWord(oCharacters[i - 1].paragraph, oCharacters[i - 1].line, oCharacters[i - 1].word, oCharacters[i - 1].character, oTextData) && oCharacters[i].character == 0) {
				
				// Get the current and previous characters.
				var oCharacter = oTextData.Paragraphs[oCharacters[i].paragraph].Lines[oCharacters[i].line].Words[oCharacters[i].word].Characters[oCharacters[i].character];
				var oPreviousCharacter = oTextData.Paragraphs[oCharacters[i - 1].paragraph].Lines[oCharacters[i - 1].line].Words[oCharacters[i - 1].word].Characters[oCharacters[i - 1].character];

				// Determine the coordinates to be used for the space based on the text direction.
				var iX1 = oPreviousCharacter.X2 < oCharacter.X1 ? oPreviousCharacter.X2 : oPreviousCharacter.X1;
				var iX2 = oPreviousCharacter.X2 < oCharacter.X1 ? oCharacter.X1 : oCharacter.X2;
				var iY1 = oPreviousCharacter.Y1 < oCharacter.Y2 ? oPreviousCharacter.Y2 : oPreviousCharacter.Y1;
				var iY2 = oPreviousCharacter.Y1 < oCharacter.Y2 ? oCharacter.Y1 : oCharacter.Y2;

				// Highlight the space between the two words.
				g_oTextTools.highlightElement(iX1, iX2, iY1, iY2, oArtworkCanvas, oAnnotationCanvas);
			}

			// Highlight the character.
			g_oTextTools.highlightCharacter(oCharacters[i].paragraph, oCharacters[i].line, oCharacters[i].word, oCharacters[i].character, oArtworkCanvas, oAnnotationCanvas);
		}
	};

	/**
	 * Gets the words for a list of sections.
	 * @param {object} oSections - The sections to get the words for.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas,
	 * @returns {object} - List of words.
	 */
	TextTools.prototype.getSectionsWords = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the words to be returned.
		var oWords = [];

		// Loop through each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Add the words for the section.
			oWords = oWords.concat(g_oTextTools.getSectionWords(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas, oAnnotationCanvas));
		}

		// Return the words.
		return oWords;
	};

	/**
	 * Checks if the text highlight is in the correct order.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the highlight is left to right.
	 * @param {bool} characterLevelAccuracy - If this is to a character level accuracy.
	 */
	TextTools.prototype.textHighlightCorrectOrder = function (startingText, endingText, leftToRight, characterLevelAccuracy) {

		// Wrong order if the end paragraph is before the start paragraph.
		if (endingText.paragraph < startingText.paragraph) {
			return false;
		}

		// Wrong order if we are on the same paragraph and the end line is before the start line.
		if (endingText.paragraph == startingText.paragraph && endingText.line < startingText.line) {
			return false;
		}

		// Wrong order if we are left to right, on the same paragraph line, but the end word is before the start word.
		if (leftToRight && endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word < startingText.word) {
			return false;
		}

		// Wrong order if we are right to left, on the same paragraph line, but the start word is before the end word.
		if (!leftToRight && endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && startingText.word < endingText.word) {
			return false;
		}

		// Wrong order if we are character left to right, on the same paragraph line word, but the end character is before the start character.
		if (characterLevelAccuracy && leftToRight && endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && startingText.word == endingText.word && endingText.character < startingText.character) {
			return false;
		}

		// Wrong order if we are character right to left, on the same paragraph line word, but the start character is before the end character.
		if (characterLevelAccuracy && !leftToRight && endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && startingText.word == endingText.word && startingText.character < endingText.character) {
			return false;
		}

		// Text is in the correct order.
		return true;
	};

	/**
	 * Gets the words for a section.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the highlight is left to right.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The list of words.
	 */
	TextTools.prototype.getSectionWords = function (startingText, endingText, leftToRight, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the words to be returned.
		var oWords = [];

		// Check the text is in the correct order.
		if (!g_oTextTools.textHighlightCorrectOrder(startingText, endingText, leftToRight, false)) {

			// Swap the order if required.
			var oStart = startingText;
			startingText = endingText;
			endingText = oStart;
		}

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the paragraphs in the document.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Extract the paragraph.
			var oParagraph = oTextData.Paragraphs[i];

			// Check the paragraph is part of this selection.
			if (i >= startingText.paragraph && i <= endingText.paragraph) {

				// Loop through each line in the paragraph.
				for (var j = 0; j < oParagraph.Lines.length; j++) {

					// Loop through each of the words on the line.
					for (var k = 0; k < oParagraph.Lines[j].Words.length; k++) {

						// If the word is in the section.
						if (g_oTextTools.isWordInSection(i, j, k, startingText, endingText, leftToRight)) {

							// Add this word to our list.
							oWords.push({ paragraph: i, line: j, word: k });
						}
					}
				}
			}
		}

		// Return the section words.
		return oWords;
	};

	/**
	 * Gets the characters for a list of sections.
	 * @param {object} oSections - The sections to get the characters for.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - List of characters.
	 */
	TextTools.prototype.getSectionsCharacters = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the characters to be returned.
		var oCharacters = [];

		// Loop through each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Add the characters for the section.
			oCharacters = oCharacters.concat(g_oTextTools.getSectionCharacters(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas, oAnnotationCanvas));
		}

		// Return the characters.
		return oCharacters;
	};

	/**
	 * Gets the characters for a section.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the highlight is left to right.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The list of characters.
	 */
	TextTools.prototype.getSectionCharacters = function (startingText, endingText, leftToRight, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the characters to be returned.
		var oCharacters = [];

		// Check the text is in the correct order.
		if (!g_oTextTools.textHighlightCorrectOrder(startingText, endingText, leftToRight, true)) {

			// Swap the order if required.
			var oStart = startingText;
			startingText = endingText;
			endingText = oStart;
		}

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the paragraphs in the document.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Extract the paragraph.
			var oParagraph = oTextData.Paragraphs[i];

			// Check the paragraph is part of this selection.
			if (i >= startingText.paragraph && i <= endingText.paragraph) {

				// Loop through each line in the paragraph.
				for (var j = 0; j < oParagraph.Lines.length; j++) {

					// Loop through each of the words on the line.
					for (var k = 0; k < oParagraph.Lines[j].Words.length; k++) {

						// Loop through each characters in the word.
						for (var l = 0; l < oParagraph.Lines[j].Words[k].Characters.length; l++) {

							// If the character is in the section.
							if (g_oTextTools.isCharacterInSection(i, j, k, l, startingText, endingText, leftToRight)) {

								// Add this character to our list.
								oCharacters.push({ paragraph: i, line: j, word: k, character: l });
							}
						}
					}
				}
			}
		}

		// Return the section characters.
		return oCharacters;
	};

	/**
	 * Performs a sort on words.
	 * @param {object} a - The first word.
	 * @param {object} b - The second word.
	 * @returns {int} - The sort.
	 */
	TextTools.prototype.sortWords = function (a, b) {
		if (a.paragraph < b.paragraph) {
			return -1;
		}
		else if (a.paragraph > b.paragraph) {
			return 1;
		}
		else if (a.line < b.line) {
			return -1;
		}
		else if (a.line > b.line) {
			return 1;
		}
		else if (a.word < b.word) {
			return -1;
		}
		else if (a.word > b.word) {
			return 1;
		}
		else {
			return 0;
		}
	};

	/**
	 * Performs a sort on characters.
	 * @param {object} a - The first character.
	 * @param {object} b - The second character.
	 * @returns {int} - The sort.
	 */
	TextTools.prototype.sortCharacters = function (a, b) {
		if (a.paragraph < b.paragraph) {
			return -1;
		}
		else if (a.paragraph > b.paragraph) {
			return 1;
		}
		else if (a.line < b.line) {
			return -1;
		}
		else if (a.line > b.line) {
			return 1;
		}
		else if (a.word < b.word) {
			return -1;
		}
		else if (a.word > b.word) {
			return 1;
		}
		else if (a.character < b.character) {
			return -1;
		}
		else if (a.character > b.character) {
			return 1;
		}
		else {
			return 0;
		}
	};

	/**
	 * Distincts a list of words.
	 * @param {object} oWords - The list of words.
	 * @returns {object} - Distinct list of words.
	 */
	TextTools.prototype.distinctWords = function (oWords) {

		// Loop through each of the words.
		for (var i = 0; i < oWords.length; i++) {

			// If this word is the same as the previous word.
			if (i > 0 && oWords[i].word == oWords[i - 1].word && oWords[i].line == oWords[i - 1].line && oWords[i].paragraph == oWords[i - 1].paragraph) {

				// Remove it from the list.
				oWords.splice(i, 1);
				i--;
			}
		}

		// Return the words.
		return oWords;
	};

	/**
	 * Distincts a list of characters.
	 * @param {object} oCharacters - The list of characters.
	 * @returns {object} - Distinct list of characters.
	 */
	TextTools.prototype.distinctCharacters = function (oCharacters) {

		// Loop through each of the characters.
		for (var i = 0; i < oCharacters.length; i++) {

			// If this word is the same as the previous character.
			if (i > 0 && oCharacters[i].character == oCharacters[i - 1].character && oCharacters[i].word == oCharacters[i - 1].word && oCharacters[i].line == oCharacters[i - 1].line && oCharacters[i].paragraph == oCharacters[i - 1].paragraph) {

				// Remove it from the list.
				oCharacters.splice(i, 1);
				i--;
			}
		}

		// Return the characters.
		return oCharacters;
	};

	/**
	 * Gets the bounds for a list of sections at word level.
	 * @param {object} oSections - The list of sections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The sections bounds.
	 */
	TextTools.prototype.getSectionsBounds_Words = function (oSections, oArtworkCanvas, oAnnotationCanvas) {
		
		// Outline the bounds to be returned.
		var oSectionsBounds = {
			x1: 1,
			x2: 0,
			y1: 1,
			y2: 0
		};

		// Loop through each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Get the bounds for the section.
			var oSectionBounds = g_oTextTools.getSectionBounds_Words(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas, oAnnotationCanvas);

			// Update the sections bounds.
			oSectionsBounds.x1 = Math.min(oSectionsBounds.x1, oSectionBounds.x1);
			oSectionsBounds.x2 = Math.max(oSectionsBounds.x2, oSectionBounds.x2);
			oSectionsBounds.y1 = Math.min(oSectionsBounds.y1, oSectionBounds.y1);
			oSectionsBounds.y2 = Math.max(oSectionsBounds.y2, oSectionBounds.y2);
		}

		// Return the bounds of the section.
		return oSectionsBounds;
	};

	/**
	 * Gets the bounds for a list of sections at character level.
	 * @param {object} oSections - The list of sections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The sections bounds.
	 */
	TextTools.prototype.getSectionsBounds_Characters = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the bounds to be returned.
		var oSectionsBounds = {
			x1: 1,
			x2: 0,
			y1: 1,
			y2: 0
		};

		// Loop through each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Get the bounds for the section.
			var oSectionBounds = g_oTextTools.getSectionBounds_Characters(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas, oAnnotationCanvas);

			// Update the sections bounds.
			oSectionsBounds.x1 = Math.min(oSectionsBounds.x1, oSectionBounds.x1);
			oSectionsBounds.x2 = Math.max(oSectionsBounds.x2, oSectionBounds.x2);
			oSectionsBounds.y1 = Math.min(oSectionsBounds.y1, oSectionBounds.y1);
			oSectionsBounds.y2 = Math.max(oSectionsBounds.y2, oSectionBounds.y2);
		}

		// Return the bounds of the section.
		return oSectionsBounds;
	};

	/**
	 * Gets the bounds of a word list.
	 * @param {object} oWordList - The list of words.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getWordListBounds = function (oWordList, oArtworkCanvas, oAnnotationCanvas) {

		// Determine the section bounds.
		var oSectionBounds = {
			x1: 1,
			x2: 0,
			y1: 1,
			y2: 0
		};

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the words.
		for (var i = 0; i < oWordList.length; i++) {

			// Extract the current word.
			var oWord = oTextData.Paragraphs[oWordList[i].paragraph].Lines[oWordList[i].line].Words[oWordList[i].word];

			// Update the section bounds.
			oSectionBounds.x1 = Math.min(oSectionBounds.x1, oWord.X1, oWord.X2);
			oSectionBounds.x2 = Math.max(oSectionBounds.x2, oWord.X1, oWord.X2);
			oSectionBounds.y1 = Math.min(oSectionBounds.y1, oWord.Y1, oWord.Y2);
			oSectionBounds.y2 = Math.max(oSectionBounds.y2, oWord.Y1, oWord.Y2);
		}

		// Return the bounds of the section.
		return oSectionBounds;
	};

	/**
	 * Gets the bounds of a character list.
	 * @param {object} oCharacterList - The list of characters.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getCharacterListBounds = function (oCharacterList, oArtworkCanvas, oAnnotationCanvas) {

		// Determine the section bounds.
		var oSectionBounds = {
			x1: 1,
			x2: 0,
			y1: 1,
			y2: 0
		};

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the characters.
		for (var i = 0; i < oCharacterList.length; i++) {

			// Extract the current character.
			var oWord = oTextData.Paragraphs[oCharacterList[i].paragraph].Lines[oCharacterList[i].line].Words[oCharacterList[i].word].Characters[oCharacterList[i].character];

			// Update the section bounds.
			oSectionBounds.x1 = Math.min(oSectionBounds.x1, oWord.X1, oWord.X2);
			oSectionBounds.x2 = Math.max(oSectionBounds.x2, oWord.X1, oWord.X2);
			oSectionBounds.y1 = Math.min(oSectionBounds.y1, oWord.Y1, oWord.Y2);
			oSectionBounds.y2 = Math.max(oSectionBounds.y2, oWord.Y1, oWord.Y2);
		}

		// Return the bounds of the section.
		return oSectionBounds;
	};

	/**
	 * Gets the bounds for a section.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the highlight is left to right.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The section bounds.
	 */
	TextTools.prototype.getSectionBounds_Words = function (startingText, endingText, leftToRight, oArtworkCanvas, oAnnotationCanvas) {

		// Determine the section bounds.
		var oSectionBounds = {
			x1: 1,
			x2: 0,
			y1: 1,
			y2: 0
		};

		// Switch the order of the start and end if required.
		if ((endingText.paragraph < startingText.paragraph) || (endingText.paragraph == startingText.paragraph && endingText.line < startingText.line) || (endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word < startingText.word)) {
			var oStart = startingText;
			startingText = endingText;
			endingText = oStart;
		}

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Extract the paragraph.
			var oParagraph = oTextData.Paragraphs[i];

			// Check the paragraph is part of this selection.
			if (i >= startingText.paragraph && i <= endingText.paragraph) {

				// Loop through each line in the paragraph.
				for (var j = 0; j < oParagraph.Lines.length; j++) {

					// Loop through each of the words on the line.
					for (var k = 0; k < oParagraph.Lines[j].Words.length; k++) {

						// Determine if the word is within the section.
						var bWordInSection = g_oTextTools.isWordInSection(i, j, k, startingText, endingText, leftToRight);

						// If the word is in the section.
						if (bWordInSection) {

							// Update the section bounds.
							oSectionBounds.x1 = Math.min(oSectionBounds.x1, oParagraph.Lines[j].Words[k].X1, oParagraph.Lines[j].Words[k].X2);
							oSectionBounds.x2 = Math.max(oSectionBounds.x2, oParagraph.Lines[j].Words[k].X1, oParagraph.Lines[j].Words[k].X2);
							oSectionBounds.y1 = Math.min(oSectionBounds.y1, oParagraph.Lines[j].Words[k].Y1, oParagraph.Lines[j].Words[k].Y2);
							oSectionBounds.y2 = Math.max(oSectionBounds.y2, oParagraph.Lines[j].Words[k].Y1, oParagraph.Lines[j].Words[k].Y2);
						}
					}
				}
			}
		}

		// Return the bounds of the section.
		return oSectionBounds;
	};

	/**
	 * Gets the bounds for a section.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the highlight is left to right.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The section bounds.
	 */
	TextTools.prototype.getSectionBounds_Characters = function (startingText, endingText, leftToRight, oArtworkCanvas, oAnnotationCanvas) {

		// Determine the section bounds.
		var oSectionBounds = {
			x1: 1,
			x2: 0,
			y1: 1,
			y2: 0
		};

		// Switch the order of the start and end if required.
		if ((endingText.paragraph < startingText.paragraph) ||
			(endingText.paragraph == startingText.paragraph && endingText.line < startingText.line) ||
			(endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word < startingText.word) ||
			(endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word == startingText.word && endingText.character < startingText.character)) {
			var oStart = startingText;
			startingText = endingText;
			endingText = oStart;
		}

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Extract the paragraph.
			var oParagraph = oTextData.Paragraphs[i];

			// Check the paragraph is part of this selection.
			if (i >= startingText.paragraph && i <= endingText.paragraph) {

				// Loop through each line in the paragraph.
				for (var j = 0; j < oParagraph.Lines.length; j++) {

					// Loop through each of the words on the line.
					for (var k = 0; k < oParagraph.Lines[j].Words.length; k++) {

						// Loop through each of the characters on the line.
						for (var l = 0; l < oParagraph.Lines[j].Words[k].Characters.length; l++) {

							// Determine if the character is within the section.
							var bCharacterInSection = g_oTextTools.isCharacterInSection(i, j, k, l, startingText, endingText, leftToRight);

							// If the character is in the section.
							if (bCharacterInSection) {

								// Update the section bounds.
								oSectionBounds.x1 = Math.min(oSectionBounds.x1, oParagraph.Lines[j].Words[k].Characters[l].X1, oParagraph.Lines[j].Words[k].Characters[l].X2);
								oSectionBounds.x2 = Math.max(oSectionBounds.x2, oParagraph.Lines[j].Words[k].Characters[l].X1, oParagraph.Lines[j].Words[k].Characters[l].X2);
								oSectionBounds.y1 = Math.min(oSectionBounds.y1, oParagraph.Lines[j].Words[k].Characters[l].Y1, oParagraph.Lines[j].Words[k].Characters[l].Y2);
								oSectionBounds.y2 = Math.max(oSectionBounds.y2, oParagraph.Lines[j].Words[k].Characters[l].Y1, oParagraph.Lines[j].Words[k].Characters[l].Y2);
							}
						}
					}
				}
			}
		}

		// Return the bounds of the section.
		return oSectionBounds;
	};

	/**
	 * Gets the word text for a word.
	 * @param {object} oWord - the word.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getWordText = function (oWord, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the word text.
		return oTextData.Paragraphs[oWord.paragraph].Lines[oWord.line].Words[oWord.word].Content;
	};

	TextTools.prototype.getCharacterText = function (oCharacter, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the character text.
		return oTextData.Paragraphs[oCharacter.paragraph].Lines[oCharacter.line].Words[oCharacter.word].Characters[oCharacter.character].Content;
	};

	/**
	 * Gets the text for a word list.
	 * @param {object} oWordList - The word list.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getWordListText = function (oWordList, oArtworkCanvas, oAnnotationCanvas) {
		
		// Outline the text to be returned.
		var szText = "";

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the words in the list.
		for (var i = 0; i < oWordList.length; i++) {

			// If this is not the first word, add a space.
			if (i > 0) {
				szText += " ";
			}

			// Add the word.
			szText += oTextData.Paragraphs[oWordList[i].paragraph].Lines[oWordList[i].line].Words[oWordList[i].word].Content;
		}

		// Return the text.
		return szText;
	};

	/**
	 * Gets the text for a character list.
	 * @param {object} oCharacterList - The character list.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getCharacterListText = function (oCharacterList, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the text to be returned.
		var szText = "";

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the characters in the list.
		for (var i = 0; i < oCharacterList.length; i++) {

			// Add the character.
			szText += oTextData.Paragraphs[oCharacterList[i].paragraph].Lines[oCharacterList[i].line].Words[oCharacterList[i].word].Characters[oCharacterList[i].character].Content;

			// If this is the last character in the word, and we have more characters to add, add a space.
			if (g_oTextTools.isLastCharacterInWord(oCharacterList[i].paragraph, oCharacterList[i].line, oCharacterList[i].word, oCharacterList[i].character, oTextData) && i + 1 < oCharacterList.length) {
				szText += " ";
			}
		}

		// Return the text.
		return szText;
	};

	/**
	 * Gets the text for a list of sections.
	 * @param {object} oSections - The list of sections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The sections text.
	 */
	TextTools.prototype.getSectionsText_Words = function (oSections, oArtworkCanvas, szPrepend, szAppend) {

		// Determine the text to be returned.
		var szSectionsText = "";

		// Add the prepend.
		szSectionsText += szPrepend;

		// Loop through each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Add the text for the section.
			szSectionsText += g_oTextTools.getSectionText_Words(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas);
		}

		// Add the append.
		szSectionsText += szAppend;

		// Return the result.
		return szSectionsText;
	};

	/**
	 * Gets the text for a section.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the section is left to right.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The section text.
	 */
	TextTools.prototype.getSectionText_Words = function (startingText, endingText, leftToRight, oArtworkCanvas) {

		// Determine the text to be returned.
		var szSectionText = "";

		// Switch the order of the start and end if required.
		if ((endingText.paragraph < startingText.paragraph) || (endingText.paragraph == startingText.paragraph && endingText.line < startingText.line) || (endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word < startingText.word)) {
			var oStart = startingText;
			startingText = endingText;
			endingText = oStart;
		}

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Extract the paragraph.
			var oParagraph = oTextData.Paragraphs[i];

			// If this paragraph is within our selection.
			if (i >= startingText.paragraph && i <= endingText.paragraph) {

				// If this paragraph was not the first paragraph, add a space before the text on the next paragraph.
				if (i > startingText.paragraph) {
					szSectionText += " ";
				}

				// Loop through each line in the paragraph.
				for (var j = 0; j < oParagraph.Lines.length; j++) {

					// If this line was not the first line, add a space before the text on the next line.
					if (i > startingText.paragraph || j > startingText.line) {
						szSectionText += " ";
					}

					// Keep track of if the previous word has been highlighted.
					var bPreviousWordHighlighted = false;

					// Loop through each of the words on the line.
					for (var k = 0; k < oParagraph.Lines[j].Words.length; k++) {

						// Determine if the word is within the section.
						var bWordInSection = g_oTextTools.isWordInSection(i, j, k, startingText, endingText, leftToRight);

						// If the word is in the section.
						if (bWordInSection) {

							// If the previous word was highlighted and has a space after.
							if (bPreviousWordHighlighted && oParagraph.Lines[j].Words[k - 1].SpaceAfter) {

								// Add the space to the text.
								szSectionText += " ";
							}

							// Add the text to the word.
							szSectionText += oParagraph.Lines[j].Words[k].Content;
						}

						// Update the previous word highlight flag.
						bPreviousWordHighlighted = bWordInSection;
					}
				}
			}
		}

		// If this is a right to left highlight, reverse the section text.
		if (!leftToRight) {
			szSectionText = szSectionText.split(" ").reverse().join(" ");
		}

		// Return the text of the section.
		return szSectionText;
	};

	/**
	 * Gets the text for a list of sections.
	 * @param {object} oSections - The list of sections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The sections text.
	 */
	TextTools.prototype.getSectionsText_Characters = function (oSections, oArtworkCanvas, szPrepend, szAppend) {

		// Determine the text to be returned.
		var szSectionsText = "";

		// Add the prepend.
		szSectionsText += szPrepend;

		// Loop through each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Add the text for the section.
			szSectionsText += g_oTextTools.getSectionText_Characters(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas);
		}

		// Add the append.
		szSectionsText += szAppend;

		// Return the result.
		return szSectionsText;
	};

	/**
	 * Gets the text for a section.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the section is left to right.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The section text.
	 */
	TextTools.prototype.getSectionText_Characters = function (startingText, endingText, leftToRight, oArtworkCanvas) {

		// Determine the text to be returned.
		var szSectionText = "";

		// Switch the order of the start and end if required.
		if ((endingText.paragraph < startingText.paragraph) ||
			(endingText.paragraph == startingText.paragraph && endingText.line < startingText.line) ||
			(endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word < startingText.word) ||
			(endingText.paragraph == startingText.paragraph && endingText.line == startingText.line && endingText.word == startingText.word && endingText.character < startingText.character)) {
			var oStart = startingText;
			startingText = endingText;
			endingText = oStart;
		}

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Loop through each of the paragraphs.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Extract the paragraph.
			var oParagraph = oTextData.Paragraphs[i];

			// If this paragraph is within our selection.
			if (i >= startingText.paragraph && i <= endingText.paragraph) {

				// If this paragraph was not the first paragraph, add a space before the text on the next paragraph.
				if (i > startingText.paragraph) {
					szSectionText += " ";
				}

				// Loop through each line in the paragraph.
				for (var j = 0; j < oParagraph.Lines.length; j++) {

					// If this line was not the first line, add a space before the text on the next line.
					if (i > startingText.paragraph || j > startingText.line) {
						szSectionText += " ";
					}

					// Loop through each of the words on the line.
					for (var k = 0; k < oParagraph.Lines[j].Words.length; k++) {

						// If this word was not the first word, add a space before the text on the next word.
						if (i > startingText.paragraph || j > startingText.line || k > startingText.word) {
							szSectionText += " ";
						}

						// Loop through each of the characters in the word.
						for (var l = 0; l < oParagraph.Lines[j].Words[k].Characters.length; l++) {

							// Determine if the character is within the section.
							var bCharacterInSection = g_oTextTools.isCharacterInSection(i, j, k, l, startingText, endingText, leftToRight);

							// If the character is in the section.
							if (bCharacterInSection) {

								// Add the text to the word.
								szSectionText += oParagraph.Lines[j].Words[k].Characters[l].Content;
							}
						}
					}
				}
			}
		}

		// If this is a right to left highlight, reverse the section text.
		if (!leftToRight) {
			szSectionText = szSectionText.split(" ").reverse().join(" ");
		}

		// Return the text of the section.
		return szSectionText;
	};

	/**
	 * Gets the bold setting for a word.
	 * @param {object} oWord - The word.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getWordBoldSetting = function (oWord, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the word bold setting.
		return oTextData.Paragraphs[oWord.paragraph].Lines[oWord.line].Words[oWord.word].Bold;
	};

	/**
	 * Gets the italic setting for a word.
	 * @param {object} oWord - The word.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getWordItalicSetting = function (oWord, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the word italic setting.
		return oTextData.Paragraphs[oWord.paragraph].Lines[oWord.line].Words[oWord.word].Italic;
	};

	/**
	 * Gets the bold setting for a character.
	 * @param {object} oCharacter - The character.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getCharacterBoldSetting = function (oCharacter, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the character bold setting.
		return oTextData.Paragraphs[oCharacter.paragraph].Lines[oCharacter.line].Words[oCharacter.word].Characters[oCharacter.character].Bold;
	};

	/**
	 * Gets the italic setting for a character.
	 * @param {object} oCharacter - The character.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getCharacterItalicSetting = function (oCharacter, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return the character italic setting.
		return oTextData.Paragraphs[oCharacter.paragraph].Lines[oCharacter.line].Words[oCharacter.word].Characters[oCharacter.character].Italic;
	};

	/**
	 * Gets if the character is the last in the word.
	 * @param {object} oCharacter - The character.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getCharacterLastInWord = function (oCharacter, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Return if the character is the last in the word.
		return g_oTextTools.isLastCharacterInWord(oCharacter.paragraph, oCharacter.line, oCharacter.word, oCharacter.character, oTextData);
	};

	/**
	 * Gets the characters for a word.
	 * @param {any} oWord - The word.
	 * @param {any} oArtworkCanvas - The artwork canvas.
	 * @param {any} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.getWordCharacters = function (oWord, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Get the word.
		var oWord = oTextData.Paragraphs[oWord.paragraph].Lines[oWord.line].Words[oWord.word];

		// Return the characters.
		return oWord.Characters;
	};

	/**
	 * Splits the sections content based on the accuracy.
	 * @param {object} oSections - The sections data.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.splitSections = function (oSections, oArtworkCanvas, oAnnotationCanvas) {
		return g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.splitSections_Characters(oSections, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.splitSections_Words(oSections, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Splits the sections content at word level.
	 * @param {object} oSections - The sections data.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.splitSections_Words = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the split sections to be build up.
		var oSplitSections = [];

		// Loop over each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Split the section into individual words.
			var oSectionWords = g_oTextTools.getSectionWords(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas, oAnnotationCanvas);

			// Loop over each of the section words and create a section for the individual word.
			for (var j = 0; j < oSectionWords.length; j++) {
				oSplitSections.push({
					startingText: {
						paragraph: oSectionWords[j].paragraph,
						line: oSectionWords[j].line,
						word: oSectionWords[j].word
					},
					endingText: {
						paragraph: oSectionWords[j].paragraph,
						line: oSectionWords[j].line,
						word: oSectionWords[j].word
					},
					leftToRight: true
				});
			}
		}

		// Return the split content.
		return oSplitSections;
	};

	/**
	 * Splits the sections content at character level.
	 * @param {object} oSections - The sections data.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.splitSections_Characters = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the split sections to be build up.
		var oSplitSections = [];

		// Loop over each of the sections.
		for (var i = 0; i < oSections.length; i++) {

			// Split the section into individual characters.
			var oSectionWords = g_oTextTools.getSectionCharacters(oSections[i].startingText, oSections[i].endingText, oSections[i].leftToRight, oArtworkCanvas, oAnnotationCanvas);

			// Loop over each of the section characters and create a section for the individual item.
			for (var j = 0; j < oSectionWords.length; j++) {
				oSplitSections.push({
					startingText: {
						paragraph: oSectionWords[j].paragraph,
						line: oSectionWords[j].line,
						word: oSectionWords[j].word,
						character: oSectionWords[j].character
					},
					endingText: {
						paragraph: oSectionWords[j].paragraph,
						line: oSectionWords[j].line,
						word: oSectionWords[j].word,
						character: oSectionWords[j].character
					},
					leftToRight: true
				});
			}
		}

		// Return the split sections.
		return oSplitSections;
	};

	/**
	 * Merges the sections based on the accuracy.
	 * @param {array} oSections - The sections to be merged.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.mergeSections = function (oSections, oArtworkCanvas, oAnnotationCanvas) {
		return g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.mergeSections_Characters(oSections, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.mergeSections_Words(oSections, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Merges the sections at a word level.
	 * @param {array} oSections - The sections to be merged.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.mergeSections_Words = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the merged sections to be build up.
		var oMergedSections = [];

		// Extract the split text data.
		var oWords = g_oTextTools.getSectionsWords(oSections, oArtworkCanvas, oAnnotationCanvas);

		// Sort the words.
		oWords = oWords.sort(g_oTextTools.sortWords);

		// Remove the duplicate words.
		oWords = g_oTextTools.distinctWords(oWords);

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Keep track of the current merged section being created.
		var oMergedSection = null;

		// Loop through each of the paragraphs on the page.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Loop through each line in the paragraph.
			for (var j = 0; j < oTextData.Paragraphs[i].Lines.length; j++) {

				// Loop through each of the words on the line.
				for (var k = 0; k < oTextData.Paragraphs[i].Lines[j].Words.length; k++) {

					// If we are on the first word, skip to the first word in the list.
					if (i == 0 && j == 0 && k == 0) {
						i = oWords[0].paragraph;
						j = oWords[0].line;
						k = oWords[0].word;
					}

					// Check if the word is in the list.
					if (g_oTextTools.isWordInList(i, j, k, oWords)) {

						// If we are building up a merged section, add the word to the ending text.
						if (oMergedSection != null) {
							oMergedSection.endingText = {
								paragraph: i, line: j, word: k
							};
						}
						// Otherwise, create a new merged section.
						else {
							oMergedSection = {
								startingText: { paragraph: i, line: j, word: k },
								endingText: { paragraph: i, line: j, word: k },
								leftToRight: true
							};
						}
					}
					// If the word is not in the list.
					else {

						// If we have a merged section being built up, add it to the merged sections.
						if (oMergedSection != null) {
							oMergedSections.push(oMergedSection);
						}

						// Reset the merged section.
						oMergedSection = null;
					}

					// If we are on the last word in the list, we can stop the search.
					if (i == oWords[oWords.length - 1].paragraph && j == oWords[oWords.length - 1].line && k == oWords[oWords.length - 1].word) {

						// If we have a merged section being built up, add it to the merged sections.
						if (oMergedSection != null) {
							oMergedSections.push(oMergedSection);
						}

						// Return the merged sections.
						return oMergedSections;
					}
				}
			}
		}

		// Return the merged sections.
		return oMergedSections;
	};

	/**
	 * Merges the sections at a character level.
	 * @param {array} oSections - The sections to be merged.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.mergeSections_Characters = function (oSections, oArtworkCanvas, oAnnotationCanvas) {

		// Outline the merged sections to be build up.
		var oMergedSections = [];

		// Extract the split text data.
		var oCharacters = g_oTextTools.getSectionsCharacters(oSections, oArtworkCanvas, oAnnotationCanvas);

		// Sort the characters.
		oCharacters = oCharacters.sort(g_oTextTools.sortCharacters);

		// Remove the duplicate characters.
		oCharacters = g_oTextTools.distinctCharacters(oCharacters);

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Keep track of the current merged section being created.
		var oMergedSection = null;

		// Loop through each of the paragraphs on the page.
		for (var i = 0; i < oTextData.Paragraphs.length; i++) {

			// Loop through each line in the paragraph.
			for (var j = 0; j < oTextData.Paragraphs[i].Lines.length; j++) {

				// Loop through each of the words on the line.
				for (var k = 0; k < oTextData.Paragraphs[i].Lines[j].Words.length; k++) {

					// Loop through each of the characters in the word.
					for (var l = 0; l < oTextData.Paragraphs[i].Lines[j].Words[k].Characters.length; l++) {

						// If we are on the first character, skip to the first character in the list.
						if (i == 0 && j == 0 && k == 0 && l == 0) {
							i = oCharacters[0].paragraph;
							j = oCharacters[0].line;
							k = oCharacters[0].word;
							l = oCharacters[0].character;
						}

						// Check if the character is in the list.
						if (g_oTextTools.isCharacterInList(i, j, k, l, oCharacters)) {

							// If we are building up a merged section, add the character to the ending text.
							if (oMergedSection != null) {
								oMergedSection.endingText = {
									paragraph: i, line: j, word: k, character: l
								};
							}
							// Otherwise, create a new merged section.
							else {
								oMergedSection = {
									startingText: { paragraph: i, line: j, word: k, character: l },
									endingText: { paragraph: i, line: j, word: k, character: l },
									leftToRight: true
								};
							}
						}
						// If the character is not in the list.
						else {

							// If we have a merged section  being built up, add it to the merged sections.
							if (oMergedSection != null) {
								oMergedSections.push(oMergedSection);
							}

							// Reset the merged section.
							oMergedSection = null;
						}

						// If we are on the last character in the list, we can stop the looping.
						if (i == oCharacters[oCharacters.length - 1].paragraph && j == oCharacters[oCharacters.length - 1].line && k == oCharacters[oCharacters.length - 1].word && l == oCharacters[oCharacters.length - 1].character) {

							// If we have a merged section being built up, add it to the merged sections.
							if (oMergedSection != null) {
								oMergedSections.push(oMergedSection);
							}

							// Return the merged sections.
							return oMergedSections;
						}
					}
				}
			}
		}

		// Return the merged sections.
		return oMergedSections;
	};

	/**
	 * Determines if a word is within a section.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the section is left to right.
	 * @returns {bool} - If the word is within the section.
	 */
	TextTools.prototype.isWordInSection = function (paragraph, line, word, startingText, endingText, leftToRight) {

		// Determine if the word is within the section based on the direction.
		return leftToRight ? g_oTextTools.isWordInSection_LeftToRight(paragraph, line, word, startingText, endingText) : g_oTextTools.isWordInSection_RightToLeft(paragraph, line, word, startingText, endingText);
	};

	/**
	 * Determines if a character is within a section.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {int} character - The character.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @param {bool} leftToRight - If the section is left to right.
	 * @returns {bool} - If the character is within the section.
	 */
	TextTools.prototype.isCharacterInSection = function (paragraph, line, word, character, startingText, endingText, leftToRight) {

		// Determine if the character is within the section based on the direction.
		return leftToRight ? g_oTextTools.isCharacterInSection_LeftToRight(paragraph, line, word, character, startingText, endingText) : g_oTextTools.isCharacterInSection_RightToLeft(paragraph, line, word, character, startingText, endingText);
	};

	/**
	 * Determines if a word is in a left to right section.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @returns {bool} - If the word is within the section.
	 */
	TextTools.prototype.isWordInSection_LeftToRight = function (paragraph, line, word, startingText, endingText) {
		
		// Determine if the word is within the section.
		var bWordInSection = false;

		// Ensure the word is after the starting text.
		if ((paragraph > startingText.paragraph) ||
			(paragraph == startingText.paragraph && line > startingText.line) ||
			(paragraph == startingText.paragraph && line == startingText.line && word >= startingText.word)) {

			// Ensure the word is before the ending text.
			if ((paragraph < endingText.paragraph) ||
				(paragraph == endingText.paragraph && line < endingText.line) ||
				(paragraph == endingText.paragraph && line == endingText.line && word <= endingText.word)) {

				// Set that the word is within the section.
				bWordInSection = true;
			}
		}

		// Return if the word is in the section.
		return bWordInSection;
	};

	/**
	 * Determines if a character is in a left to right section.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {int} character - The character.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @returns {bool} - If the character is within the section.
	 */
	TextTools.prototype.isCharacterInSection_LeftToRight = function (paragraph, line, word, character, startingText, endingText) {

		// Determine if the character is within the section.
		var bCharacterInSection = false;

		// Ensure the character is after the starting text.
		if ((paragraph > startingText.paragraph) ||
			(paragraph == startingText.paragraph && line > startingText.line) ||
			(paragraph == startingText.paragraph && line == startingText.line && word > startingText.word) ||
			(paragraph == startingText.paragraph && line == startingText.line && word == startingText.word && character >= startingText.character)) {

			// Ensure the character is before the ending text.
			if ((paragraph < endingText.paragraph) ||
				(paragraph == endingText.paragraph && line < endingText.line) ||
				(paragraph == endingText.paragraph && line == endingText.line && word < endingText.word) ||
				(paragraph == endingText.paragraph && line == endingText.line && word == endingText.word && character <= endingText.character)) {

				// Set that the character is within the section.
				bCharacterInSection = true;
			}
		}

		// Return if the character is in the section.
		return bCharacterInSection;
	};

	/**
	 * Determines if a word is in a right to left section.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @returns {bool} - If the word is within the section.
	 */
	TextTools.prototype.isWordInSection_RightToLeft = function (paragraph, line, word, startingText, endingText) {
		
		// Determine if the word is within the section.
		var bWordInSection = false;

		// Ensure the word is after the starting text.
		if ((paragraph > startingText.paragraph) ||
			(paragraph == startingText.paragraph && line > startingText.line) ||
			(paragraph == startingText.paragraph && line == startingText.line && word <= startingText.word)) {

			// Ensure the word is before the ending text.
			if ((paragraph < endingText.paragraph) ||
				(paragraph == endingText.paragraph && line < endingText.line) ||
				(paragraph == endingText.paragraph && line == endingText.line && word >= endingText.word)) {

				// Set that the word is within the section.
				bWordInSection = true;
			}
		}

		// If the starting text and ending text are on the same line, we need to check the switched direction for words inbetween.
		if (startingText.paragraph == endingText.paragraph && startingText.line == endingText.line &&
			paragraph == endingText.paragraph && line == endingText.line && word >= startingText.word && word <= endingText.word) {

			// Set that the word is within the section.
			bWordInSection = true;
		}

		// Return if the word is in the section.
		return bWordInSection;
	};

	/**
	 * Determines if a character is in a right to left section.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {int} character - The character.
	 * @param {object} startingText - The starting text.
	 * @param {object} endingText - The ending text.
	 * @returns {bool} - If the character is within the section.
	 */
	TextTools.prototype.isCharacterInSection_RightToLeft = function (paragraph, line, word, character, startingText, endingText) {

		// Determine if the character is within the section.
		var bCharacterInSection = false;

		// Ensure the character is after the starting text.
		if ((paragraph > startingText.paragraph) ||
			(paragraph == startingText.paragraph && line > startingText.line) ||
			(paragraph == startingText.paragraph && line == startingText.line && word < startingText.word) ||
			(paragraph == startingText.paragraph && line == startingText.line && word == startingText.word && character <= startingText.character)) {

			// Ensure the character is before the ending text.
			if ((paragraph < endingText.paragraph) ||
				(paragraph == endingText.paragraph && line < endingText.line) ||
				(paragraph == endingText.paragraph && line == endingText.line && word > endingText.word) ||
				(paragraph == endingText.paragraph && line == endingText.line && word == endingText.word && character >= endingText.character)) {

				// Set that the character is within the section.
				bCharacterInSection = true;
			}
		}

		// If the starting text and ending text are on the same line, we need to check the switched direction for characters inbetween.
		if (startingText.paragraph == endingText.paragraph && startingText.line == endingText.line && startingText.word == endingText.word &&
			paragraph == endingText.paragraph && line == endingText.line && word == endingText.word &&
			character >= startingText.character && character <= endingText.character) {

			// Set that the character is within the section.
			bCharacterInSection = true;
		}

		// Return if the character is in the section.
		return bCharacterInSection;
	};

	/**
	 * Determines if a word is in a list of words.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {array} oWordList - The word list.
	 */
	TextTools.prototype.isWordInList = function (paragraph, line, word, oWordList) {
		return $.grep(oWordList, function (oWordListItem) {
			return oWordListItem.paragraph == paragraph && oWordListItem.line == line && oWordListItem.word == word;
		}).length > 0;
	};

	/**
	 * Determines if a character is in a list of characters.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {int} character - The character.
	 * @param {array} oCharacterList - The character list.
	 */
	TextTools.prototype.isCharacterInList = function (paragraph, line, word, character, oCharacterList) {
		return $.grep(oCharacterList, function (oCharacterListItem) {
			return oCharacterListItem.paragraph == paragraph && oCharacterListItem.line == line && oCharacterListItem.word == word && oCharacterListItem.character == character;
		}).length > 0;
	};

	/**
	 * Highlights a paragraph.
	 * @param {int} paragraph - The paragraph.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightParagraph = function (paragraph, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Extract the paragrpah to be highlighted.
		var oParagraph = oTextData.Paragraphs[paragraph];

		// Highlight the element.
		g_oTextTools.highlightElement(oParagraph.X1, oParagraph.X2, oParagraph.Y1, oParagraph.Y2, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Highlights a line.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightLine = function (paragraph, line, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Extract the line to be highlighted.
		var oLine = oTextData.Paragraphs[paragraph].Lines[line];

		// Highlight the element.
		g_oTextTools.highlightElement(oLine.X1, oLine.X2, oLine.Y1, oLine.Y2, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Highlights a word.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightWord = function (paragraph, line, word, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;
		var oWord = oTextData.Paragraphs[paragraph].Lines[line].Words[word];

		// Highlight the element.
		g_oTextTools.highlightElement(oWord.X1, oWord.X2, oWord.Y1, oWord.Y2, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Highlights a character.
	 * @param {int} paragraph - The paragraph.
	 * @param {int} line - The line.
	 * @param {int} word - The word.
	 * @param {int} character - The character.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightCharacter = function (paragraph, line, word, character, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;
		var oCharacter = oTextData.Paragraphs[paragraph].Lines[line].Words[word].Characters[character];

		// Highlight the element.
		g_oTextTools.highlightElement(oCharacter.X1, oCharacter.X2, oCharacter.Y1, oCharacter.Y2, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Highlights an element.
	 * @param {double} X1 - The x1 of the element.
	 * @param {double} X2 - The x2 of the element.
	 * @param {double} Y1 - The y1 of the element.
	 * @param {double} Y2 - The y2 of the element.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.highlightElement = function (X1, X2, Y1, Y2, oArtworkCanvas, oAnnotationCanvas) {

		// Get the canvas scaled image width and height.
		var iScaledImageWidth = oArtworkCanvas.g_iScaledImageWidth;
		var iScaledImageHeight = oArtworkCanvas.g_iScaledImageHeight;

		// Calculate the compare offset on the artwork canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(oArtworkCanvas);

		// If we have a compare rotation.
		if (iCompareRotation > 0) {

			// If we have a compare rotation of 90 or 270, swap the scaled image width and height.
			if (iCompareRotation == 90 || iCompareRotation == 270) {
				iScaledImageWidth = oArtworkCanvas.g_iScaledImageHeight;
				iScaledImageHeight = oArtworkCanvas.g_iScaledImageWidth;
			}

			// Rotate the lower left positions.
			var oRotatedLetterLower = oAnnotationCanvas.rotate(X1, Y1, 0.5, 0.5, iCompareRotation);
			X1 = oRotatedLetterLower.rotated_x;
			Y1 = oRotatedLetterLower.rotated_y;

			// Rotate the upper right positions.
			var oRotatedLetterUpper = oAnnotationCanvas.rotate(X2, Y2, 0.5, 0.5, iCompareRotation);
			X2 = oRotatedLetterUpper.rotated_x;
			Y2 = oRotatedLetterUpper.rotated_y;
		}

		// Calculate the start positions and distance.
		var startX = (X1 * iScaledImageWidth) + oArtworkCanvas.g_iCenterShift_x + compareOffset_x;
		var startY = (Y2 * iScaledImageHeight) + oArtworkCanvas.g_iCenterShift_y + compareOffset_y;
		var distX = ((X2 - X1) * iScaledImageWidth);
		var distY = ((Y1 - Y2) * iScaledImageHeight);

		// Draw the highlight.
		oAnnotationCanvas.drawTextHighlight(oAnnotationCanvas.g_oContext, startX, startY, distX, distY, g_oTextTools.g_szHighlightColour);
	};

	/**
	 * Sets the highlight colour.
	 * @param {string} colour - The colour to be used.
	 */
	TextTools.prototype.setHighlightColour = function (colour) {

		// If this is already an RGBA colour.
		if (colour.indexOf("rgba(") == 0) {
			g_oTextTools.g_szHighlightColour = colour;
			return;
		}

		// Ensure we are in a hex colour.
		var oHexColour = g_oAnnotations.getHexColour(colour);

		// Convert the hex colour to RGB.
		var oRGB = convertColourHexToRGB(oHexColour);

		// Update the highlight colour.
		g_oTextTools.g_szHighlightColour = "rgba(" + oRGB.r + ", " + oRGB.g + ", " + oRGB.b + ", " + 0.5 + ")";
	};

	/**
	 * Creates a text highlight annotation.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.createAnnotation = function (oArtworkCanvas, oAnnotationCanvas) {

		// Generate a text annotation.
		var oTextAnnotation = g_oTextTools.generateTextAnnotation(g_oTextTools.g_oAnnotationText, g_oTextTools.g_eActiveAnnotationType, oArtworkCanvas, oAnnotationCanvas);

		// Add the annotation to the annotations list.
		g_oAnnotations.g_oAnnotationsMade.push(oTextAnnotation);

		// Create the annotation div.
		oTextAnnotation.createAnnotationDiv(eAnnotationDivType.Popup, true, null, oArtworkCanvas, oAnnotationCanvas);

		// Redraw the annotations.
		oAnnotationCanvas.g_oContext.clearRect(0, 0, oAnnotationCanvas.g_oCanvas.width, oAnnotationCanvas.g_oCanvas.height);
		g_oAnnotations.redrawAnnotations(oArtworkCanvas, oAnnotationCanvas);

		// Reposition the speech bubbles.
		g_oAnnotations.repositionSpeechBubbles(oArtworkCanvas, oAnnotationCanvas);

		// Focus the JQTE text editor for the new annotation.
		$(".jqte_editor").focus();
	};

	/**
	 * Generates a text annotation.
	 * @param {object} oAnnotationText - The annotation text.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 * @returns {object} - The text annotation.
	 */
	TextTools.prototype.generateTextAnnotation = function (oAnnotationText, eAnnotationType, oArtworkCanvas, oAnnotationCanvas) {

		// Create a new annotation for the text.
		var oTextAnnotation = new Annotation(g_oAnnotations.g_iApprovalId);

		// Set the id of the annotation.
		oTextAnnotation.id = g_oAnnotations.getNextAnnotationId();

		// Set the annotation type.
		oTextAnnotation.g_iAnnotationType = eAnnotationType;

		// Set the draw array from the bounds.
		var oSectionBounds = g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getSectionsBounds_Characters(oAnnotationText, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.getSectionsBounds_Words(oAnnotationText, oArtworkCanvas, oAnnotationCanvas);
		oTextAnnotation.g_oDrawArray = [[oSectionBounds.x1, oSectionBounds.y1], [oSectionBounds.x2, oSectionBounds.y2]];

		// Set the text for the annotation.
		oTextAnnotation.g_szTextContent = g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getSectionsText_Characters(oAnnotationText, oArtworkCanvas, "<div>", "</div>") : g_oTextTools.getSectionsText_Words(oAnnotationText, oArtworkCanvas, "<div>", "</div>");

		// Set the text highlight content for the annotation.
		oTextAnnotation.g_szTextHighlightContent = JSON.stringify(oAnnotationText);

		// Populate the properties of the annotation.
		oTextAnnotation.g_iParentAnnotationId = 0;
		oTextAnnotation.g_iNumber = Math.max(1, Math.max.apply(Math, g_oAnnotations.g_oAnnotationsMade.map(function (o) { return o.g_iNumber; })) + 1);
		oTextAnnotation.g_szColour = g_oAnnotations.getHexColour(g_oAnnotations.g_oAnnotationColSelected);
		oTextAnnotation.g_iDocumentId = oArtworkCanvas.g_oDoc.documentId;
		oTextAnnotation.g_iAuthor = g_oUserDetails.UserId;
		oTextAnnotation.g_szAuthorEmail = g_oUserDetails.EmailAddress;
		oTextAnnotation.g_bSaved = false;
		oTextAnnotation.status = "a";

		// If we have more than one page, grab the page id by looping through the pages.
		if (!$.isEmptyObject(oArtworkCanvas.g_oDoc.pages)) {
			for (var i = 0; i < oArtworkCanvas.g_oDoc.pages.length; i++) {
				if (oArtworkCanvas.g_oDoc.pages[i].documentPageNumber == oArtworkCanvas.g_iCurrentPage) {
					oTextAnnotation.g_iDocumentPageId = oArtworkCanvas.g_oDoc.pages[i].documentPageId;
					break;
				}
			}
		}

		// If this is a text classification annotation, populate the annotation data.
		if (eAnnotationType == eAnnotationTypes.TextClassification) {
			oTextAnnotation.g_oAnnotationData = g_oAnnotationTextClassification.populateDefaultAnnotationData();
		}

		// Return the generated SVG text annotation.
		return oTextAnnotation;
	};

	/**
	 * Updates a text highlight annotation.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.updateAnnotation = function (oArtworkCanvas, oAnnotationCanvas) {
		
		// Find the annotation in the list.
		var oTextAnnotation = $.grep(g_oAnnotations.g_oAnnotationsMade, function (ann) {
			return ann.id == g_oTextTools.g_iExistingAnnotationId;
		})[0];

		// Set the draw array from the bounds, and update the coordinates.
		var oSectionBounds = g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getSectionsBounds_Characters(g_oTextTools.g_oAnnotationText, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.getSectionsBounds_Words(g_oTextTools.g_oAnnotationText, oArtworkCanvas, oAnnotationCanvas);
		oTextAnnotation.g_oDrawArray = [[oSectionBounds.x1, oSectionBounds.y1], [oSectionBounds.x2, oSectionBounds.y2]];
		oTextAnnotation.g_oDrawCoordSetArray[0].xCoordinate = oSectionBounds.x1;
		oTextAnnotation.g_oDrawCoordSetArray[0].yCoordinate = oSectionBounds.y1;
		oTextAnnotation.g_oDrawCoordSetArray[1].xCoordinate = oSectionBounds.x2;
		oTextAnnotation.g_oDrawCoordSetArray[1].xCoordinate = oSectionBounds.y2;

		// Update the text content for the annotation.
		oTextAnnotation.g_szTextContent = g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getSectionsText_Characters(g_oTextTools.g_oAnnotationText, oArtworkCanvas, "<div>", "</div>") : g_oTextTools.getSectionsText_Words(g_oTextTools.g_oAnnotationText, oArtworkCanvas, "<div>", "</div>");

		// Set the text highlight content for the annotation.
		oTextAnnotation.g_szTextHighlightContent = JSON.stringify(g_oTextTools.g_oAnnotationText);

		// Set the correct annotation status.
		oTextAnnotation.status = oTextAnnotation.status == "a" ? "a" : "u";

		// Save the annotation.
		oTextAnnotation.AnnotationSave(eAnnotationDivType.Popup, oArtworkCanvas, oAnnotationCanvas, true);
	};

	/**
	 * Updates a new text highlight annotation.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextTools.prototype.updateNewAnnotation = function (oArtworkCanvas, oAnnotationCanvas) {

		// Find the annotation in the list.
		var oTextAnnotation = $.grep(g_oAnnotations.g_oAnnotationsMade, function (ann) {
			return ann.id == g_oTextTools.g_iExistingAnnotationId;
		})[0];

		// Set the draw array from the bounds.
		var oSectionBounds = g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getSectionsBounds_Characters(g_oTextTools.g_oAnnotationText, oArtworkCanvas, oAnnotationCanvas) : g_oTextTools.getSectionsBounds_Words(g_oTextTools.g_oAnnotationText, oArtworkCanvas, oAnnotationCanvas);
		oTextAnnotation.g_oDrawArray = [[oSectionBounds.x1, oSectionBounds.y1], [oSectionBounds.x2, oSectionBounds.y2]];

		// Update the text content for the annotation.
		oTextAnnotation.g_szTextContent = g_oTextTools.g_bHighlightIsCharacterAccuracy ? g_oTextTools.getSectionsText_Characters(g_oTextTools.g_oAnnotationText, oArtworkCanvas, "<div>", "</div>") : g_oTextTools.getSectionsText_Words(g_oTextTools.g_oAnnotationText, oArtworkCanvas, "<div>", "</div>");

		// Set the text highlight content for the annotation.
		oTextAnnotation.g_szTextHighlightContent = JSON.stringify(g_oTextTools.g_oAnnotationText);

		// Create the annotation div.
		oTextAnnotation.createAnnotationDiv(eAnnotationDivType.Popup, true, null, oArtworkCanvas, oAnnotationCanvas);

		// Redraw the annotations.
		oAnnotationCanvas.g_oContext.clearRect(0, 0, oAnnotationCanvas.g_oCanvas.width, oAnnotationCanvas.g_oCanvas.height);
		g_oAnnotations.redrawAnnotations(oArtworkCanvas, oAnnotationCanvas);

		// Reposition the speech bubbles.
		g_oAnnotations.repositionSpeechBubbles(oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Determines if the text highlight is left to right.
	 * @returns {bool} - If the text highlight is left to right.
	 */
	TextTools.prototype.isTextHighlightLeftToRight = function () {
		return g_oSettings.g_oUserSettings.HighlightPreference == eHighlightType.LeftToRight;
	};

	// Add the Text Tools UI object to the window.
	window.TextTools = TextTools;
}());